import React, { useRef, useState } from "react"
import { Button, Upload, Input } from "antd"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import classNames from "classnames"
import { Icon } from "ui/Icon"
import { X } from "react-feather"
import { useTranslation } from "react-i18next"
import { colors } from "themes/variables"
import { notification } from "ui/Notification"
import { clearEditingMessage } from "store/conversations"
import { validateFile } from "utils/validate-media"
import PreviewMedia from "components/common/PreviewMedia"
import { LoadingOverlay } from "components/common/LoadingOverlay"

MessageEditor.propTypes = {
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  uploadClassName: PropTypes.string,
  media: PropTypes.object,
  loading: PropTypes.bool,
  setMedia: PropTypes.func.isRequired,
  messageValue: PropTypes.string,
  placeholder: PropTypes.string,
  subscription: PropTypes.object,
  conversation: PropTypes.object,
  user: PropTypes.object,
  imageContainer: PropTypes.string,
  onKeyDown: PropTypes.func,
  setMessageValue: PropTypes.func.isRequired
}

const { TextArea } = Input
export default function MessageEditor({
  className,
  media,
  loading,
  setMedia,
  messageValue,
  placeholder = "",
  inputClassName = "",
  uploadClassName = "",
  subscription,
  conversation,
  user,
  imageContainer = "",
  onKeyDown,
  setMessageValue
}) {
  const isClient=conversation?.client?.is_client
  const { t } = useTranslation()
  const [isTyping, setIsTyping] = useState(false) // State to track typing
  const [isFocused, setIsFocused] = useState(false)
  const typingTimeoutRef = useRef(null)
  const dispatch = useDispatch()
  const editingState = useSelector((state) => ({
    editingMessageId: state.conversations.editingMessageId,
    editingMessageBody: state.conversations.editingMessageBody
  }))
  const { editingMessageId } = editingState

  const handlePastedFiles = (files) => {
    const file = files[0]
    const { isValid, type } = validateFile(file, t)
    if (!isValid) return
    const mediaUrl = URL.createObjectURL(file)
    const mediaFile = {
      url: mediaUrl,
      type,
      file
    }
    // Append the new media file to the existing media array
    setMedia((prevMedia) => [...(prevMedia || []), mediaFile])
  }

  const handleChangeEditor = (e) => {
    if (isFocused) {
      const newValue = e.target.value.replace(/^ /, "") // remove only the first space
      setMessageValue(newValue)
    }

    if (!isTyping) {
      setIsTyping(true)
      subscription?.conversationSubscription.send({
        event: "typing",
        conversation_id: conversation.id,
        typing: true,
        current_user: user.id,
        is_client:isClient
      })
    }

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false)
      subscription?.conversationSubscription.send({
        event: "typing",
        conversation_id: conversation.id,
        typing: false,
        current_user: user.id,
        is_client:isClient
      })
    }, 4000)
  }

  const onUpload = ({ file }) => {
    const currentMediaCount = media?.length || 0

    if (currentMediaCount > 4) {
      notification.error({
        message: t("messenger.errors.tooManyFiles")
      })
      return
    }
    handlePastedFiles([file])
  }

  const handleRemove = (url) => {
    setMedia((prevMedia) => prevMedia.filter((item) => item.url !== url))
  }

  return (
    <div className={classNames(className)}>
      <Upload beforeUpload={(file) => false} showUploadList={false} onChange={onUpload}>
        <Button
          className={classNames(uploadClassName)}
          imageClassname="rounded"
          icon={<Icon name="Image" color={colors.textSecondaryDark} width={16} height={16} />}
        />
      </Upload>
      <div className={classNames(inputClassName)}>
        <LoadingOverlay isLoading={loading}>
          <div className={classNames(imageContainer)}>
            {media?.map((item) => (
              <PreviewMedia
                key={item.url}
                type={item.type}
                preview={item.url} // Pass the URL as a string
                onClose={() => handleRemove(item.url)} // Remove the specific media file
                className=""
                mediaClassName="message__img-preview"
              />
            ))}
          </div>
        </LoadingOverlay>
        <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
          {/* Wrapper for icon and TextArea */}
          {editingMessageId && (
            <X
              style={{
                position: "absolute",
                left: "8px",
                color: "var(--color-secondary)",
                zIndex: 1,
                cursor: "pointer",
                top: "15px",
                height: "16px"
              }}
              onClick={() => {
                setMessageValue("")
                dispatch(clearEditingMessage())
              }} // Clear media on click
            />
          )}
          <TextArea
            id="message"
            name="message"
            value={messageValue}
            onChange={handleChangeEditor}
            placeholder={placeholder || t("messenger.placeholder")}
            handlePastedFiles={handlePastedFiles}
            onKeyDown={onKeyDown}
            autoSize
            rows={4}
            style={{
              maxHeight: "200px",
              minHeight: "40px",
              paddingLeft: editingMessageId ? "32px" : "" // Add padding to make space for the icon
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              console.log("called")
              setIsFocused(false)}}
          />
        </div>
      </div>
    </div>
  )
}
